import { useMemo, useEffect } from 'react';
import { PAYMENT_TYPES } from '@sdk/account/constants';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import { YEAR_MONTHS, getBillingDate, getIsYearlyPlan } from '@web/account/utils';
import { usePurchaseContext } from '@web/account/purchase/PurchaseProvider/PurchaseProvider';
import { SubscriptionUtilsType } from '@web/account/types';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { usePricingBreakdown } from '@web/account/hooks/usePricingBreakdown';

type Props = {
  subscriptionUtils: SubscriptionUtilsType;
  currentUser: AuthUserType;
};

export const usePurchaseView = ({ subscriptionUtils, currentUser }: Props) => {
  const {
    monthlyPricePerUserPerMonth,
    setMonthlyPricePerUserPerMonth,
    subscriptionLevel,
    taxPercent,
  } = usePurchaseContext();
  const { billingInfo } = useBillingInfo();
  const { subscriptionBillingCycle } = usePurchaseContext();

  const isYearlyPlan = getIsYearlyPlan(subscriptionBillingCycle);

  useEffect(() => {
    const monthlyPrice = subscriptionUtils.getPricePerUnit(subscriptionLevel, false);
    setMonthlyPricePerUserPerMonth(monthlyPrice);
  }, [subscriptionUtils, subscriptionLevel, setMonthlyPricePerUserPerMonth]);

  const yearlyPricePerUserPerMonth = useMemo(
    () => subscriptionUtils.getPricePerUnit(subscriptionLevel, true) / YEAR_MONTHS,
    [subscriptionUtils, subscriptionLevel]
  );

  const yearlyPlanDiscount =
    (monthlyPricePerUserPerMonth - yearlyPricePerUserPerMonth) * YEAR_MONTHS * billingInfo.quantity;

  const { priceSummary } = usePricingBreakdown({
    isYearlyPlan,
    ignoreNextCharge: false,
    subscriptionLevel,
    subscriptionUtils,
    taxPercent,
    isSubtotalWithDiscount: false,
  });

  const billedOnDate = useMemo(
    () => getBillingDate(billingInfo, currentUser),
    [billingInfo, currentUser]
  );

  const isShopifyBilling = billingInfo.paymentType === PAYMENT_TYPES.SHOPIFY_BILLING;

  return {
    billedOnDate,
    isShopifyBilling,
    monthlyPricePerUserPerMonth,
    priceSummary,
    yearlyPlanDiscount,
    yearlyPricePerUserPerMonth,
  };
};
